import BaseHttp from './BaseHttp';
import config from '../../config/app.config';
import { CARS_LIMIT } from '../framework/constants/cars';
import { MODULE_SURFACE, PAGE_TYPE } from '../framework/constants/tracking';
const STATS_MAX_CAR_IDS = 40;
class UserActionsAPI extends BaseHttp {
    constructor(config) {
        super();
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        // ADV-259: pageType tags the RECENTLY_VIEWED impression this fires server-side (see
        // user-actions RecentController). Defaults to OTHER, NOT Homepage — this method is also
        // called from the account dashboard's "Recently viewed" section (RecentlyStore.ts via
        // RecentlySection.tsx), which is neither Homepage nor Landing Page; only NewMainPage.tsx's
        // Homepage call site overrides this explicitly.
        Object.defineProperty(this, "getRecentlyCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (pageType = PAGE_TYPE.OTHER) => {
                return this.privateRequest('get', `recents?pageType=${pageType}`);
            }
        });
        Object.defineProperty(this, "sendingTemplate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (dataObj) => this.privateRequest('post', 'description/template', dataObj)
        });
        Object.defineProperty(this, "getAllTemplates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => this.privateRequest('get', `description/templates/${id}`)
        });
        Object.defineProperty(this, "updateTemplate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id, dataObj) => this.privateRequest('put', `description/template/${id}`, dataObj)
        });
        Object.defineProperty(this, "deleteTemplate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => this.privateRequest('delete', `description/template/${id}`)
        });
        // Get number of favourite vehicles
        Object.defineProperty(this, "getFavoritesCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (userID) => this.privateRequest('get', `user/${userID}/favorite/count`)
        });
        Object.defineProperty(this, "getFavoritesIDs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => this.privateRequest('get', `user/favorite/car_id`)
        });
        // Add vehicle to favorites
        Object.defineProperty(this, "addFavorite", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carID) => this.privateRequest('post', `user/favorite/car/${carID}`)
        });
        // Remove vehicle from favorites
        Object.defineProperty(this, "removeFavorite", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carID) => {
                return this.privateRequest('delete', `user/favorite/car/${carID}`);
            }
        });
        // Get list of favorite cars
        Object.defineProperty(this, "getFavoriteCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (offset = 0, sort = '') => {
                return this.privateRequest('get', `user/favorite/car`, { sort, offset, limit: CARS_LIMIT })
                    .then((response) => {
                    return response.data;
                });
            }
        });
        // Add favorite cars from anonim user to authorized
        Object.defineProperty(this, "moveFavoritesToUserAccount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this.privateRequest('post', 'user/favorite/to-favorites');
            }
        });
        // Get list of vehicles for compare page
        Object.defineProperty(this, "getComparedVehicles", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carsIDs, isDiff) => {
                const diff = isDiff ? '?onlyDifference=true' : '';
                return this.privateRequest('post', `car/comparison/list${diff}`, { id: carsIDs })
                    .then((response) => {
                    return response.data;
                });
            }
        });
        // Get list of recommendation cars
        Object.defineProperty(this, "getRecommendationCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId) => {
                return this.privateRequest('get', `car/${carId}/recommendation`)
                    .then((response) => {
                    return response.data ? response.data : [];
                });
            }
        });
        Object.defineProperty(this, "copyCar", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId) => {
                return this.privateRequest('post', `car/${carId}/duplicate`);
            }
        });
        Object.defineProperty(this, "copyCarForDealer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId, newData) => {
                return this.privateRequest('post', `car/${carId}/duplicate`, newData)
                    .then((response) => {
                    return response.data;
                });
            }
        });
        Object.defineProperty(this, "restoreCarForSuperAdmin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (newData) => {
                return this.privateRequest('post', `car/restore`, newData)
                    .then((response) => {
                    return response.data;
                });
            }
        });
        // stats/viewers takes at most 40 car ids (user-actions CarViewerController, 400 "To much
        // carIds" above that): a longer list, e.g. a long "Kürzlich angesehen" history, goes in batches
        // and is answered as one { data } in the same order. A failed batch fails the whole call.
        Object.defineProperty(this, "getStatsData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carIds) => {
                if (!Array.isArray(carIds) || carIds.length <= STATS_MAX_CAR_IDS) {
                    return this.request('post', `stats/viewers`, { carIds });
                }
                const batches = [];
                for (let i = 0; i < carIds.length; i += STATS_MAX_CAR_IDS)
                    batches.push(carIds.slice(i, i + STATS_MAX_CAR_IDS));
                return Promise.all(batches.map((batch) => this.request('post', `stats/viewers`, { carIds: batch })))
                    .then((responses) => ({
                    data: responses.reduce((all, response) => all.concat((response && response.data) || []), []),
                }));
            }
        });
        Object.defineProperty(this, "addSearchString", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (searchString, chipList, query, emailAlert) => {
                // BE requires data to be wrapped in data: {}
                return this.privateRequest('post', 'search-string', { emailAlert, data: { ...searchString, chipList, query } });
            }
        });
        Object.defineProperty(this, "getSearchString", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => this.privateRequest('get', 'search-string')
        });
        Object.defineProperty(this, "updateSearchString", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (searchStringId, data) => this.privateRequest('put', `search-string/${searchStringId}`, data)
        });
        Object.defineProperty(this, "deleteSearchString", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (searchStringId) => this.privateRequest('delete', `search-string/${searchStringId}`)
        });
        // ADV-259: same OTHER-by-default reasoning as getRecentlyCars above — this is called from
        // Homepage (Top offers, module=TOP_OFFERS default) AND as a VDP fallback when there are no
        // per-car "similar to this one" results (VehicleDetailsStore.ts, module=SIMILAR_CARS) AND
        // from the 404 page (NotFoundStore.ts, left as OTHER/TOP_OFFERS default — no dedicated
        // module for that surface yet). Only Homepage's own call site overrides pageType explicitly.
        Object.defineProperty(this, "getPublicRecommendation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (pageType = PAGE_TYPE.OTHER, moduleSurface = MODULE_SURFACE.TOP_OFFERS) => this.privateRequest('get', `user/favorite/cars/top?limit=20&days=30&pageType=${pageType}&module=${moduleSurface}`)
                .then((response) => {
                return response.data;
            })
        });
        this.setConfig(config);
    }
}
export default new UserActionsAPI({
    baseURL: config.userActionsAPI.url
});
